"use client";

import { useRef, useState } from "react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { cards } from "./cards";
import ToolMock from "./ToolMock";
import Image from "next/image";

const ToolkitShowcase = () => {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const scrollToCard = (index) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(cards.length - 1, index));
    const child = track.children[clamped];
    if (child) {
      track.scrollTo({ left: child.offsetLeft, behavior: "smooth" });
      setActive(clamped);
    }
  };

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const index = Math.round(track.scrollLeft / (track.clientWidth * 0.92));
    setActive(Math.max(0, Math.min(cards.length - 1, index)));
  };

  return (
    <section
      className="relative bg-[#06070C] px-5 py-16 mt-20 mb-6 sm:px-6 sm:py-20 lg:px-20 lg:py-24"
      style={{
        backgroundImage:
          "radial-gradient(25% 35% at 20% 20%, rgba(79,61,240,0.12) 0%, rgba(79,61,240,0.06) 20%, transparent 70%)",
      }}
    >
      <img
        src="/assets/testingOptimization/black-edge.svg"
        alt="black-edge"
        className="absolute top-0 left-0 z-10 w-full -translate-y-[55%]"
      />

      <img
        src="/assets/testingOptimization/black-edge.svg"
        alt="black-edge"
        className="absolute bottom-0 left-0 z-10 w-full translate-y-[55%] rotate-180"
      />

      {/* ── Header ── */}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-white" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
              Inside the Toolkit · Scroll
            </span>
            <ArrowRight size={13} className="text-white" />
          </div>
          <h2 className="mt-4 max-w-[700px] text-[26px] font-medium leading-[1.12] tracking-[-0.5px] text-white sm:text-[34px] lg:text-[44px] 3xl:text-[52px]">
            Four tools, photographed{" "}
            <span className="relative inline-block">
              <span className="absolute inset-x-[-4px] top-[50%] bottom-0 z-0 bg-gradient-to-r from-[#7F7FFF]/40 to-[#7F7FFF]/30" />
              <span className="relative z-10">at work</span>
            </span>
          </h2>
        </div>

        <div className="flex items-center flex-col gap-4">
          <div className="flex items-center gap-4">
            <span className="text-[13px] font-medium tabular-nums text-white/50">
              <span className="text-white">
                {String(active + 1).padStart(2, "0")}
              </span>{" "}
              / {String(cards.length).padStart(2, "0")}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Previous tool"
                onClick={() => scrollToCard(active - 1)}
                disabled={active === 0}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/15 text-white/70 transition-colors hover:border-white/40 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ArrowLeft size={16} />
              </button>
              <button
                type="button"
                aria-label="Next tool"
                onClick={() => scrollToCard(active + 1)}
                disabled={active === cards.length - 1}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/15 text-white/70 transition-colors hover:border-white/40 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map((i) =>
              String(active + 1).padStart(2, "0") ===
              String(i).padStart(2, "0") ? (
                <span key={i} className="inline-block h-px w-8 bg-white" />
              ) : (
                <span key={i} className="inline-block h-px w-8 bg-white/10" />
              ),
            )}
          </div>
        </div>
      </div>

      {/* ── Cards ── */}
      <div
        ref={trackRef}
        onScroll={handleScroll}
        className="mt-12 flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] lg:mt-14 [&::-webkit-scrollbar]:hidden"
      >
        {cards.map((card) => (
          <article
            key={card.no}
            className="relative grid w-full shrink-0 snap-start grid-cols-1 overflow-hidden border border-[#030303] bg-[#141415] lg:min-w-[100%] lg:grid-cols-2"
          >
            {/* top + bottom gradient hairlines (from Figma) */}
            <span className="pointer-events-none absolute inset-x-0 top-0 z-10 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
            <span className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

            {/* Copy */}
            <div className="flex flex-col p-7 sm:p-9 lg:p-12">
              <div className="flex items-center gap-3">
                <span className="text-[34px] font-semibold leading-none tracking-[-1px] text-white lg:text-[42px]">
                  {card.no}
                </span>
                <span className="font-serif text-[12px] italic tracking-[0.05em] text-white/35">
                  // {card.kicker}
                </span>
              </div>

              <h3 className="mt-5  text-[30px] font-medium leading-[1.05] tracking-[-0.5px] text-white lg:text-[38px]">
                {card.title}
              </h3>
              <p className="mt-4 max-w-[420px] text-[13px] leading-[1.6] text-white/55">
                {card.desc}
              </p>

              <div className="mt-8 flex flex-col">
                {card.rows.map((row, idx) => (
                  <div
                    key={row.title}
                    className="flex items-start gap-3.5 border-t border-white/10 py-4 first:border-t-0 first:pt-0"
                  >
                    <span className="mt-0.5 shrink-0 text-[11px] font-medium tabular-nums text-white/35">
                      {String(idx + 1).padStart(2, "0")}.
                    </span>
                    <div>
                      <h4 className="text-[14px] font-semibold tracking-[-0.2px] text-white">
                        {row.title}
                      </h4>
                      <p className="mt-1 max-w-[380px] text-[12px] leading-[1.55] text-white/45">
                        {row.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <a
                href="https://app.imagive.ai/"
                target="_blank"
                className="group mt-auto inline-flex w-fit items-center gap-1.5 border-b border-white/20 pb-1 pt-9 text-[12px] font-medium uppercase tracking-[0.12em] text-white/70 transition-colors hover:border-white/60 hover:text-white"
              >
                {card.cta}
                <ArrowRight
                  size={13}
                  className="transition-transform duration-300 group-hover:translate-x-0.5"
                />
              </a>
            </div>

            {/* Mock panel */}
            <div className="border-t border-white/10 bg-[linear-gradient(142deg,#FFFFFF06_0%,#0000002E_100%)] lg:border-l lg:border-t-0">
              <ToolMock no={card.no} />
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default ToolkitShowcase;
